import React, { useMemo, useState } from 'react';
import { brandTheme, brandThemeCss, isHexColor, type ShopperTheme } from '../../utils/brandTheme';
import '../../styles/shopper.css';

/**
 * A brand's look on its public pages (#462), as the public APIs send it:
 * logo URLs and two `#rrggbb` colours, each optional.
 */
export interface PublicBrand {
  logoSquareUrl?: string;
  logoWideUrl?: string;
  primaryColor?: string;
  accentColor?: string;
}

/** The brand's colours on every shopper surface of the page, sheets included. */
export const BrandColours: React.FC<{ brand?: PublicBrand | null }> = ({ brand }) => {
  const css = useMemo(() => brandThemeCss(brand?.primaryColor, brand?.accentColor), [brand?.primaryColor, brand?.accentColor]);
  return css ? <style data-brand-colours="">{css}</style> : null;
};

/** The mat's base colour for the browser chrome, or undefined for BasiQR's. */
export function brandBackground(brand: PublicBrand | null | undefined, theme: ShopperTheme): string | undefined {
  if (!isHexColor(brand?.primaryColor) || !isHexColor(brand?.accentColor)) return undefined;
  return brandTheme(brand!.primaryColor!, brand!.accentColor!, theme).background;
}

/**
 * A logo that hides itself when its image doesn't load, so a broken upload
 * falls back to whatever the caller shows without one.
 */
function useLogo(url?: string) {
  const [failed, setFailed] = useState<string | null>(null);
  return { ok: !!url && failed !== url, onError: () => setFailed(url ?? null) };
}

/** The logo a brand's label carries, picked by useLabelLogo. */
export interface LabelLogo {
  src: string;
  /** The square logo, there being no wide one that loads. */
  square: boolean;
  onError: () => void;
}

/**
 * The logo for the brand's label: the wide one, else the square one, leaving
 * out any that doesn't load. Null when there is none, so a page can show
 * something else in its place, elsewhere too (the line sheet's initial).
 */
export function useLabelLogo(brand?: PublicBrand | null): LabelLogo | null {
  const wide = useLogo(brand?.logoWideUrl);
  const square = useLogo(brand?.logoSquareUrl);
  if (wide.ok) return { src: brand!.logoWideUrl!, square: false, onError: wide.onError };
  if (square.ok) return { src: brand!.logoSquareUrl!, square: true, onError: square.onError };
  return null;
}

/**
 * A logo from useLabelLogo on a woven label: any logo reads on it, on the
 * light and the dark mat. Renders `fallback` when there is no logo.
 */
export const LogoLabel: React.FC<{
  logo: LabelLogo | null;
  name: string;
  className?: string;
  fallback?: React.ReactNode;
}> = ({ logo, name, className = '', fallback = null }) => {
  if (!logo) return <>{fallback}</>;
  return (
    <span className={`brand-label ${logo.square ? 'is-square' : ''} ${className}`.trim()}>
      <img src={logo.src} alt={name} onError={logo.onError} />
    </span>
  );
};

/**
 * The brand's wide logo on a woven label (the square one when there is no
 * wide one). Renders `fallback` when the brand has no logo, or it doesn't load.
 */
export const BrandLabel: React.FC<{
  brand?: PublicBrand | null;
  name: string;
  className?: string;
  fallback?: React.ReactNode;
}> = ({ brand, ...label }) => {
  const logo = useLabelLogo(brand);
  return <LogoLabel logo={logo} {...label} />;
};

/**
 * The brand's wide logo as a plain image, for cloth that already is a label
 * (the vault card's care label), or its name when it has none.
 */
export const BrandLogoOrName: React.FC<{ brand?: PublicBrand | null; name: string }> = ({ brand, name }) => {
  const logo = useLogo(brand?.logoWideUrl);
  if (!logo.ok) return <>{name}</>;
  return <img className="brand-wordmark" src={brand!.logoWideUrl} alt={name} onError={logo.onError} />;
};

/**
 * The brand's square logo on a small woven plate, or `fallback` when it has
 * none (or it doesn't load).
 */
export const BrandMark: React.FC<{
  brand?: PublicBrand | null;
  size?: number;
  className?: string;
  fallback?: React.ReactNode;
}> = ({ brand, size = 36, className = '', fallback = null }) => {
  const logo = useLogo(brand?.logoSquareUrl);
  if (!logo.ok) return <>{fallback}</>;
  return (
    <span className={`brand-mark ${className}`.trim()} style={{ width: size, height: size }} aria-hidden="true">
      <img src={brand!.logoSquareUrl} alt="" onError={logo.onError} />
    </span>
  );
};
